// frontend/src/components/charts/KpiValue.jsx
// KPI tile: value + change chip, which says on hover what the change
// compares with.

import React from 'react';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormatters } from '../../hooks/useFormatters';
import StatusChip from '../common/StatusChip';

// `change` is the backend KPI's `{kind, value}` (reporting.kpi_comparison.
// kpi_change): a `percent` of what there was, or the `amount` that appeared
// from nothing, in the tile's currency. `comparison` names what it is
// measured against — the backend KPI that fills `change` decides it.
function KpiValue({ kpiValue, change, currency, comparison, invertColors, neutralColors }) {
  const { formatCompact, formatNumber } = useFormatters();
  const { t } = useTranslation();

  const amount = change == null ? null : Number(change.value);
  const changeColor = neutralColors
    ? 'neutral'
    : invertColors
      ? (amount > 0 ? 'error' : amount < 0 ? 'success' : 'neutral')
      : (amount > 0 ? 'success' : amount < 0 ? 'error' : 'neutral');
  const sign = amount > 0 ? '+' : '';
  const chipText = {
    percent: () => `${sign}${formatNumber(amount, 2)}%`,
    amount: () => `${sign}${formatCompact(change.value, currency)}`,
  };
  if (change != null && !chipText[change.kind]) {
    throw new Error(`KPI payload carries an unknown change kind (got ${change.kind})`);
  }
  const changeText = change == null ? null : chipText[change.kind]();
  const comparedWith = {
    samePeriodLastMonth: () => t('home.comparedWith.samePeriodLastMonth'),
    endOfLastMonth: () => t('home.comparedWith.endOfLastMonth'),
  }[comparison]();

  return (
    <div className="metric-display">
      <span className="metric-value">{formatCompact(kpiValue, currency)}</span>
      {changeText && (
        <Tooltip title={comparedWith}>
          <StatusChip label={changeText} color={changeColor} />
        </Tooltip>
      )}
    </div>
  );
}

export default KpiValue;
